"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Send2, Copy } from "iconsax-react";
import { formatNumberWithSuffix } from "@/lib/formatters";
import { useState } from "react";
import { shareVideoById } from "@/actions/videos";
import { SupportedLanguage } from "@/lib/shared";
import lang from "@/app/[locale]/(main)/lang.json";
import { cn } from "@/lib/utils";

type ShareDialogProps = {
  videoId: string;
  shares: number;
  videoURL: string;
  onShareCountChange?: (newCount: number) => void;
  children?: React.ReactNode;
  locale?: SupportedLanguage;
  triggerIconClassName?: string;
  reelRailMode?: boolean;
};

export default function ShareDialog({
  videoId,
  shares,
  videoURL,
  onShareCountChange,
  children,
  locale = "en",
  triggerIconClassName,
  reelRailMode = false,
}: ShareDialogProps) {
  const [isOpen, isOpenSet] = useState(false);
  const [isSubmitting, isSubmittingSet] = useState(false);
  const [copied, copiedSet] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(videoURL);
      copiedSet(true);
      setTimeout(() => copiedSet(false), 1500);

      const result = await shareVideoById(videoId);
      if (result.status === "success") {
        onShareCountChange?.(shares + 1);
      }
    } catch (e) {
      console.log({ e });

      // ignore
    }
  };

  const handleNativeShare = async () => {
    if (typeof navigator !== "undefined" && "share" in navigator) {
      try {
        await navigator.share({
          title: "Check out this video",
          url: videoURL,
        });

        // Call API to track share if user didn't cancel
        const result = await shareVideoById(videoId);
        if (result.status === "success") {
          onShareCountChange?.(shares + 1);
        }
      } catch (e) {
        console.log({ e });
        // User cancelled or error occurred
      }
    }
  };

  const handleWhatsAppShare = async () => {
    const text = encodeURIComponent(`Check out this video: ${videoURL}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");

    // Call API to track share
    try {
      const result = await shareVideoById(videoId);
      if (result.status === "success") {
        onShareCountChange?.(shares + 1);
      }
    } catch (e) {
      console.log({ e });
      // no-op
    }
  };

  const handleFacebookShare = async () => {
    const url = encodeURIComponent(videoURL);
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      "_blank"
    );

    // Call API to track share
    try {
      const result = await shareVideoById(videoId);
      if (result.status === "success") {
        onShareCountChange?.(shares + 1);
      }
    } catch (e) {
      console.log({ e });
      // no-op
    }
  };

  const handleShareNow = async () => {
    if (isSubmitting) return;
    isSubmittingSet(true);
    try {
      const result = await shareVideoById(videoId);
      if (result.status === "success") {
        onShareCountChange?.(shares + 1);
      }
      // Close after slight delay to give feedback
      setTimeout(() => {
        isOpenSet(false);
      }, 300);
    } catch (e) {
      console.log({ e });
      // no-op
    } finally {
      isSubmittingSet(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={isOpenSet}>
      <DialogTrigger asChild>
        {children ?? (
          <button
            type="button"
            className="flex flex-col cursor-pointer items-center justify-center gap-1"
          >
          <span
            className={cn(
              "rounded-full p-2",
              reelRailMode ? "" : "bg-gray-200",
            )}
          >
              <Send2
                size={24}
                className={cn("fill-current", triggerIconClassName)}
                variant={
                  reelRailMode ? "Outline" : shares > 0 ? "Bold" : "Outline"
                }
              />
            </span>
            <span
              className={cn(
                shares > 0 ? "text-sm" : "text-xs",
                reelRailMode && "text-white",
              )}
            >
              {shares > 0 ? formatNumberWithSuffix(shares) : lang["share"][locale]}
            </span>
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share this video</DialogTitle>
          <DialogDescription>
            Choose how you&apos;d like to share this video.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Copy Link */}
          <div className="flex items-center gap-2">
            <div className="flex-1 truncate text-ellipsis max-w-[300px] rounded-md border px-3 py-2 text-sm text-muted-foreground">
              {videoURL || "Video link will appear here"}
            </div>
            <Button variant="secondary" size="sm" onClick={handleCopy}>
              <Copy size={16} className="stroke-current" variant="Outline" />
              <span className="ml-2 text-xs">{copied ? "Copied" : "Copy"}</span>
            </Button>
          </div>

          {/* Social Share Options */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Share to:</p>
            <div className="grid grid-cols-2 gap-2">
              {typeof navigator !== "undefined" && "share" in navigator && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNativeShare}
                  className="justify-start"
                >
                  <Send2
                    size={16}
                    className="stroke-current mr-2"
                    variant="Outline"
                  />
                  Native Share
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleWhatsAppShare}
                className="justify-start bg-green-50 hover:bg-green-100 border-green-200"
              >
                <span className="mr-2 text-green-600">📱</span>
                WhatsApp
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleFacebookShare}
                className="justify-start bg-blue-50 hover:bg-blue-100 border-blue-200"
              >
                <span className="mr-2 text-blue-600">📘</span>
                Facebook
              </Button>
            </div>
          </div>

          {/* Share Now Action */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t">
            <Button variant="ghost" onClick={() => isOpenSet(false)}>
              Cancel
            </Button>
            <Button onClick={handleShareNow} disabled={isSubmitting}>
              <Send2
                size={16}
                className="fill-current mr-2"
                variant="Outline"
              />
              {isSubmitting ? "Sharing..." : "Share now"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
